import { CommonModule, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import { NgModule, provideZoneChangeDetection } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { AutoRedemptionSettingsModule } from '@features/auto-redemption-settings/auto-redemption-settings.module';
import { AutoRedemptionFormDataGuard } from '@features/auto-redemption-settings/guards/auto-redemption-form-data-guard/auto-redemption-form-data.guard.service';
import { CartItemModule } from '@features/cart-item/cart-item.module';
import { EnrollmentModule } from '@features/enrollment/enrollment.module';
import { EverydaySpendModule } from '@features/everyday-spend/everyday-spend.module';
import { provideEverydaySpendEnabled } from '@features/everyday-spend/tokens/unleash-flags.token';
import { LoyaltyProgramModule } from '@features/loyalty-program/loyalty-program.module';
import { MembershipModule } from '@features/membership/membership.module';
import { OrderCheckoutModule } from '@features/order-checkout/order-checkout.module';
import { PaymentInitEffects } from '@features/payment/effects/payment-init.effects';
import { PhysicalAddressModule } from '@features/physical-address/physical-address.module';
import { PhysicalAddressConfiguration } from '@features/physical-address/types';
import { PointsAccountModule } from '@features/points-account/points-account.module';
import { PointsSummaryModule } from '@features/points-summary/points-summary.module';
import { RedeemCashV2Module } from '@features/redeem-cash-v2/redeem-cash-v2.module';
import { UserPreferenceHeaderInterceptor } from '@features/user-preference/interceptors/user-preference-header/user-preference-header.interceptor';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { DayJsConfig } from '@rd-lib/dayjs.config';
import { RdLibModule } from '@rd-lib/rd-lib.module';
import { RdLibModuleHermesModeConfig } from '@rd-lib/rd-lib.module.config';
import { RD_UNLEASH_CONFIG } from '@rd-lib/unleash-configs';
import { CmsModule } from '@rd/cms/cms.module';
import { AutoRedemptionFeatureGuard } from '@rd/core/guards/auto-redemption-feature/auto-redemption-feature.guard';
import { MerchandiseFeatureGuard } from '@rd/core/guards/merchandise-feature/merchandise-feature.guard';
import { ShoppingCartFeatureGuard } from '@rd/core/guards/shopping-cart-feature/shopping-cart-feature.guard';
import { NydusNetworkAuthHeaderInterceptor } from '@rd/core/interceptors/nydus-network-auth-header/nydus-network-auth-header.interceptor';
import { generateMFAModuleConfig, MfaModule } from '@rd/core/mfa/mfa.module';
import { MFARedirectUrlKeys } from '@rd/core/mfa/types';
import { provideUnleashProxyWithUserContext } from '@rd/core/providers/unleash-initializer.provider';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { FeatureFlagService } from '@rd/core/services/feature-flag/feature-flag.service';
import { PaymentGatewayService } from '@rd/core/services/payment-gateway/payment-gateway.service';
import {
  BUTTON_LINK_ICON,
  EXTERNAL_LINK_ICON,
  INTERNAL_LINK_ICON,
} from '@rd/core/tokens/link.token';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { EnrollmentHelper } from '@rd/helpers/enrollment.helper';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { SharedModule } from '@rd/shared-legacy/shared.module';
import { AnzAppComponent } from '@rd/wl/anz/app.component';
import { AnzAuthSecureRouteDialogComponent } from '@rd/wl/anz/components/auth-secure-route-dialog/auth-secure-route-dialog.component';
import { AnzMembershipEffects } from '@rd/wl/anz/effects/anz-membership-effects.service';
import { AnzVelocityAutoRedemptionEnrollmentEffects } from '@rd/wl/anz/effects/anz-velocity-auto-redemption-enrollment-effects.service';
import { ApplicationEffects } from '@rd/wl/anz/effects/application-effects';
import { FormSubmissionEffects } from '@rd/wl/anz/effects/form-submission-effects.service';
import { AnzEnrollmentHelper } from '@rd/wl/anz/helpers/anz-enrollment.helper';
import { AnzAnalyticsService } from '@rd/wl/anz/services/analytics/analytics.service';
import { AnzBizNavLinksService } from '@rd/wl/anz/services/anz-biz-nav-links.service';
import { ANZFeatureFlagService } from '@rd/wl/anz/services/anz-feature-flag.service';
import { AnzHomePageService } from '@rd/wl/anz/services/anz-home-page.service';
import { AnzNavLinksService } from '@rd/wl/anz/services/anz-nav-links.service';
import { AnzPaymentGatewayService } from '@rd/wl/anz/services/payment-gateway/payment-gateway.service';
import { AnzPopupSettingService } from '@rd/wl/anz/services/popup-setting/popup-setting.service';
import { AnzModule } from '@rd/wl/anz/shared/anz.module';
import {
  ANZ_SSO_ENABLED,
  provideAnzDisablePhysicalGiftCard,
} from '@rd/wl/anz/tokens/features.tokens';
import { AnzTransferConnectV2Module } from '@rd/wl/anz/transfer-connect-v2';
import dayjs from 'dayjs';
import dayjsLocaleEnAU from 'dayjs/locale/en-au';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { externalModules, externalProviders } from '../build-specifics';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AnzBizAutoRedemptionEffects } from './effects/anz-biz-auto-redemption-effects';
import { AnzBizShoppingCartUserPreferenceEffects } from './effects/anz-biz-shopping-cart-user-preference.effects';
import { AnzBizHomePageService } from './services/anz-home-page.service';
import { reducers } from './store';

registerLocaleData(localeEn, 'en-AU');
dayjs.extend(advancedFormat);
dayjs.extend(updateLocale);
dayjs.extend(relativeTime, DayJsConfig.relativeTimeConfig);
dayjs.extend(utc);
dayjs.updateLocale('en-au', {
  relativeTime: {
    ...dayjsLocaleEnAU.relativeTime,
    d: '1 business day',
    dd: '%d business days',
    y: '1 year',
  },
});

const declaredComponents: any[] = [];

// copied from bootstrap api
const supportedLocales: string[] = ['en-AU'];

@NgModule({
  declarations: [...declaredComponents],
  imports: [
    CmsModule,
    PointsSummaryModule,
    PointsAccountModule,
    OrderCheckoutModule,
    AutoRedemptionSettingsModule,
    AnzModule,
    MfaModule.forRoot(
      generateMFAModuleConfig({
        [MFARedirectUrlKeys.ShoppingCart]: {
          rdRedirectUrl: '/shopping-cart/payment',
          ghRedirectUrl: '/shopping-cart/home',
        },
        [MFARedirectUrlKeys.CashbackOneTimeRedemption]: {
          rdRedirectUrl: null,
          ghRedirectUrl: '/redeem_cash/checkout',
        },
        [MFARedirectUrlKeys.CashBackAutoRedemption]: {
          rdRedirectUrl: null,
          ghRedirectUrl: '/redeem_cash/checkout',
        },
        [MFARedirectUrlKeys.DigitalGiftCardAutoRedemption]: {
          rdRedirectUrl: null,
          ghRedirectUrl: '/gift_card/listing',
        },
        [MFARedirectUrlKeys.PhysicalGiftCardAutoRedemption]: {
          rdRedirectUrl: null,
          ghRedirectUrl: '/gift_card/listing',
        },
        [MFARedirectUrlKeys.PointsTransferAutoRedemption]: {
          rdRedirectUrl: null,
          ghRedirectUrl: '/transfer/programs',
        },
        [MFARedirectUrlKeys.EverydaySpendAutoRedemption]: {
          rdRedirectUrl: null,
          ghRedirectUrl: '/everyday_rewards',
        },
      }),
    ),
    EnrollmentModule,
    MembershipModule,
    LoyaltyProgramModule,
    CommonModule,
    BrowserModule,
    SharedModule,
    RedeemCashV2Module,
    EverydaySpendModule,
    AnzTransferConnectV2Module,
    RdLibModule.forRoot({
      defaultCurrencyCode: 'AUD',
      defaultLocaleCode: 'en-AU',
      nydusNetworkApiBaseUrl: environment.nydusNetworkApiBaseUrl,
      env: environment.env,
      production: environment.production,
      guardhouseApiUrl: environment.guardhouseApiUrl,
      clientId: environment.clientId,
      connection: environment.connection,
      scope: environment.scope,
      redirectUri: environment.redirectUri,
      travelEdgeBaseUrl: environment.travelEdgeBaseUrl,
      complimentaryNightBaseUrl: null,
      brandName: environment.brandName,
      portalName: 'ANZ Business Rewards',
      shortBrandName: environment.shortBrandName,
      stripeApiKey: environment.stripeApiKey,
      tenantName: 'anz-biz',
      hermesMode: environment.hermesMode as RdLibModuleHermesModeConfig,
      idleCheckConfig: environment.idleCheckConfig,
      includeClosingPointsAccounts: true,
      sessionCheckPollInterval: environment.sessionCheckPollInterval,
      cookiesConfig: environment.cookiesConfig,
      supportedLocales,
      pointsTransactionEnabled: true,
      cashNumberDigitsInfo: '1.0-2',
      pointsNumberDigitsInfo: '1.0-2',
      pointsTransactionDigitsInfo: '1.2-2',
      prismicConfig: environment.prismicConfig,
      useCustomPageTitle: true,
      appId: environment.appId,
    }),
    AppRoutingModule,
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([
      ApplicationEffects,
      AnzBizShoppingCartUserPreferenceEffects,
      AnzBizAutoRedemptionEffects,
      AnzMembershipEffects,
      AnzVelocityAutoRedemptionEnrollmentEffects,
      FormSubmissionEffects,
      PaymentInitEffects,
    ]),
    CartItemModule,
    externalModules,
    PhysicalAddressModule.forRoot({
      physicalAddressConfig:
        environment.physicalAddressConfig as PhysicalAddressConfiguration,
    }),
  ],
  providers: [
    provideZoneChangeDetection(),
    { provide: EnrollmentHelper, useClass: AnzEnrollmentHelper },
    { provide: PopupSettingService, useClass: AnzPopupSettingService },
    { provide: AnzHomePageService, useClass: AnzBizHomePageService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NydusNetworkAuthHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserPreferenceHeaderInterceptor,
      multi: true,
    },
    {
      provide: AnzNavLinksService,
      useClass: AnzBizNavLinksService,
    },
    {
      provide: AnalyticsService,
      useClass: AnzAnalyticsService,
    },
    { provide: PaymentGatewayService, useClass: AnzPaymentGatewayService },
    AutoRedemptionFeatureGuard,
    AutoRedemptionFormDataGuard,
    {
      provide: FeatureFlagService,
      useClass: ANZFeatureFlagService,
    },
    ShoppingCartFeatureGuard,
    MerchandiseFeatureGuard,
    {
      provide: CORE_AUTH_SECURE_ROUTE_DIALOG,
      useValue: AnzAuthSecureRouteDialogComponent,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        hideRequiredMarker: true,
      },
    },
    {
      provide: BUTTON_LINK_ICON,
      useValue: 'icon-chevron-right',
    },
    {
      provide: EXTERNAL_LINK_ICON,
      useValue: 'icon-read-more',
    },
    {
      provide: INTERNAL_LINK_ICON,
      useValue: 'icon-read-more',
    },
    provideUnleashProxyWithUserContext({
      ...RD_UNLEASH_CONFIG,
      appName: environment.unleashAppName,
      clientKey: environment.unleashClientKey,
    }),
    {
      provide: ANZ_SSO_ENABLED,
      useValue: false,
    },
    provideAnzDisablePhysicalGiftCard(),
    provideEverydaySpendEnabled(),
    ...externalProviders,
  ],
  bootstrap: [AnzAppComponent],
})
export class AppModule {}
